import { invariant } from '@mimir/common/util/invariant';
import type { FuzzyValue } from '@mimir/common/util/fuzzyNumbers';
import { addFuzzy, compareFuzzy, divideFuzzy, fractionToFuzzy, lessThanFuzzy, multiplyFuzzy, subtractFuzzy } from '@mimir/common/util/fuzzyMath';
import type { StartPoint } from '@mimir/common/util/item';
import type { MediaSelection, TimeRange } from '@mimir/common/util/timeRange';
import { frac } from '@mimir/common/util/integerMath';

export type MarkPoint = 'mark-in' | 'mark-out';

const MINIMUM_PARTIAL_DURATION = 100;
export default class Timeline {
  private _sourceInPosition: FuzzyValue | null = null;
  private _sourceOutPosition: FuzzyValue | null = null;
  inMarkCallback?: () => void = undefined;  // invoked when sourceInPosition changes
  outMarkCallback?: () => void = undefined; // invoked when sourceOutPosition changes

  isDragging = false;
  marksFromTranscriptSelection = false;

  private startPointInternal: StartPoint = {
    offsetInMs: 0,
    showAsLocalTime: false,
  };

  constructor() { }

  // Getter and setter for sourceInPosition with automatic callback triggering
  get sourceInPosition(): FuzzyValue | null {
    return this._sourceInPosition;
  }

  set sourceInPosition(value: FuzzyValue | null) {
    const changed = this._sourceInPosition?.approximate !== value?.approximate;
    this._sourceInPosition = value;
    if (changed && this.inMarkCallback) {
      this.inMarkCallback();
    }
  }

  // Getter and setter for sourceOutPosition with automatic callback triggering
  get sourceOutPosition(): FuzzyValue | null {
    return this._sourceOutPosition;
  }

  set sourceOutPosition(value: FuzzyValue | null) {
    const changed = this._sourceOutPosition?.approximate !== value?.approximate;
    this._sourceOutPosition = value;
    if (changed && this.outMarkCallback) {
      this.outMarkCallback();
    }
  }

  getSelectedSourceMediaRange(virtualClipRange: MediaSelection): MediaSelection {
    let whole = virtualClipRange.whole;

    let start = virtualClipRange.bounds.start;
    let end = virtualClipRange.bounds.end;
    if (this.sourceInPosition != null && compareFuzzy(start, this.sourceInPosition) != 0) {
      start = this.sourceInPosition;
      whole = false;
    }

    if (this.sourceOutPosition != null && compareFuzzy(end, this.sourceOutPosition) != 0) {
      end = this.sourceOutPosition;
      whole = false;
    }

    return { whole, bounds: { start, end } };
  }

  getTimeRange(virtualClipDurationInMs: number, virtualClipStart: FuzzyValue | null) {
    return this.hasMarks ? { start: this.getMarkIn(virtualClipStart), end: this.getMarkOut(virtualClipDurationInMs, virtualClipStart) } : null;
  }

  getPartialDurationInMsOrNull(virtualClipDurationInMs: number, virtualClipStart: FuzzyValue | null): number | null {
    if (!this.hasMarks) return null;
    return this.getMarkOut(virtualClipDurationInMs, virtualClipStart) - this.getMarkIn(virtualClipStart);
  }

  getPartialDurationInMs(virtualClipDurationInMs: number, virtualClipStart: FuzzyValue | null): number {
    if (!this.hasMarks) return virtualClipDurationInMs;
    return this.getMarkOut(virtualClipDurationInMs, virtualClipStart) - this.getMarkIn(virtualClipStart);
  }

  hasMarkIn(): boolean {
    return this.sourceInPosition !== null;
  }

  hasMarkOut(): boolean {
    return this.sourceOutPosition !== null;
  }

  get hasMarks() {
    return this.hasMarkIn() || this.hasMarkOut();
  }

  // Read only
  getMarkIn(virtualClipStart: FuzzyValue | null) {
    if (this.sourceInPosition != null) {
      const clipStart = virtualClipStart;
      if (clipStart != null) {
        const clipInPosition = subtractFuzzy(this.sourceInPosition, clipStart);
        return clipInPosition.approximate * 1000;
      }
    }

    return 0;
  }

  getMarkOut(virtualClipDurationInMs: number, virtualClipStart: FuzzyValue | null) {
    if (this.sourceOutPosition != null) {
      const clipStart = virtualClipStart;
      if (clipStart != null) {
        const clipInPosition = subtractFuzzy(this.sourceOutPosition, clipStart);
        return clipInPosition.approximate * 1000;
      }
    }

    return virtualClipDurationInMs;
  }

  /**
   * Get the media position that identifies the start of the last sample before
   * the out boundary.
   */
  getStartOfLastSelectedSample(virtualClipRange: MediaSelection, sampleDuration: FuzzyValue | null) {
    if (sampleDuration == null) {
      return null;
    }
    return subtractFuzzy(this.getSelectedSourceMediaRange(virtualClipRange).bounds.end, sampleDuration);
  }

  getCanMarkIn(
    durationInMs: number,
    virtualClipStart: FuzzyValue | null,
    nominalSampleStart: FuzzyValue | null
  ) {
    return this.isSampleInsideVirtualClip(durationInMs, virtualClipStart, nominalSampleStart);
  }

  getCanMarkOut(
    durationInMs: number,
    virtualClipStart: FuzzyValue | null,
    nominalSampleStart: FuzzyValue | null
  ) {
    return this.isSampleInsideVirtualClip(durationInMs, virtualClipStart, nominalSampleStart);
  }

  /**
   * TODO: Consider removing this, and instead rely on the timeSource not
   * offering samples which is not within the confines of the virtual clip.
   */
  private isSampleInsideVirtualClip(
    durationInMs: number,
    virtualClipStart: FuzzyValue | null,
    nominalSampleStart: FuzzyValue | null
  ) {
    const clipStart = virtualClipStart;
    const sampleStart = nominalSampleStart;
    if (clipStart == null || sampleStart == null || lessThanFuzzy(sampleStart, clipStart)) {
      return false;
    }

    // TODO: Obtain virtual clip end from timeSource
    const clipEnd = addFuzzy(clipStart, { approximate: durationInMs / 1000 });
    return lessThanFuzzy(sampleStart, clipEnd);
  }


  resetMarks() {
    this.sourceInPosition = null;
    this.sourceOutPosition = null;
  }

  resetInMark() {
    this.sourceInPosition = null;
  }

  resetOutMark() {
    this.sourceOutPosition = null;
  }

  setMarksFromTime(
    virtualClipDurationInMs: number,
    virtualClipStart: FuzzyValue | null,
    range: TimeRange | null,
    fromTranscriptSelection: boolean = false
  ) {
    if (range === null) {
      this.resetMarks();
      return;
    }
    this.marksFromTranscriptSelection = fromTranscriptSelection;
    this.setMarkInFromTimeInternal(virtualClipDurationInMs, virtualClipStart, range.start);
    this.setMarkOutFromTimeInternal(virtualClipDurationInMs, virtualClipStart, range.end);
  }

  getMarks(virtualClipDurationInMs: number, virtualClipStart: FuzzyValue | null): TimeRange {
    return {
      start: this.getMarkIn(virtualClipStart),
      end: this.getMarkOut(virtualClipDurationInMs, virtualClipStart),
    };
  }

  /*
    TODO: Consider removing. Is only used by unit tests, and is not used by
    actual production code.
  */
  setMarkInFromTime(
    virtualClipDurationInMs: number,
    virtualClipStart: FuzzyValue | null,
    ms: number,
  ) {
    this.setMarkInFromTimeInternal(virtualClipDurationInMs, virtualClipStart, ms);
    return ms;
  }

  setInMarkCallback(callback: () => void) {
    this.inMarkCallback = callback;
  }

  setOutMarkCallback(callback: () => void) {
    this.outMarkCallback = callback;
  }

  setInMark(nominalSampleStart: FuzzyValue | null) {
    const sampleStart = nominalSampleStart;
    invariant(sampleStart != null, 'Cannot set in mark without selected sample');
    this.setInMarkAtSampleBoundary(sampleStart);
  }

  /**
   * Adjust the in-point, snapping to sample boundaries, and limiting it to
   * before any out-mark.
   */
  adjustInMarkNearVirtualClipPosition(
    virtualClipStart: FuzzyValue | null,
    sampleDuration: FuzzyValue | null,
    virtualClipRange: MediaSelection,
    virtualClipPosition: FuzzyValue
  ) {
    let sampleBoundary = this.tryGetSampleBoundaryNearVirtualClipPosition(virtualClipStart, sampleDuration, virtualClipRange, virtualClipPosition);
    if (sampleBoundary == null) {
      return null;
    }

    if (this.sourceOutPosition != null && !lessThanFuzzy(sampleBoundary, this.sourceOutPosition)) {
      if (sampleDuration == null) {
        return;
      }
      sampleBoundary = subtractFuzzy(this.sourceOutPosition, sampleDuration);
    }

    this.sourceInPosition = sampleBoundary;
  }

  setOutMark(sampleDuration: FuzzyValue | null, nominalSampleStart: FuzzyValue | null) {
    const sampleStart = nominalSampleStart;
    invariant(sampleStart != null && sampleDuration != null, 'Cannot set in mark without selected sample');
    this.setOutMarkAtSampleBoundary(addFuzzy(sampleStart, sampleDuration));
  }

  /**
   * Adjust the out-point, snapping to sample boundaries, and limiting it to
   * after any in-mark.
   */
  adjustOutMarkNearVirtualClipPosition(
    virtualClipStart: FuzzyValue | null,
    sampleDuration: FuzzyValue | null,
    virtualClipRange: MediaSelection,
    virtualClipPosition: FuzzyValue
  ) {
    let sampleBoundary = this.tryGetSampleBoundaryNearVirtualClipPosition(virtualClipStart, sampleDuration, virtualClipRange, virtualClipPosition);
    if (sampleBoundary == null) {
      return null;
    }

    if (this.sourceInPosition != null && !lessThanFuzzy(this.sourceInPosition, sampleBoundary)) {
      if (sampleDuration == null) {
        return;
      }
      sampleBoundary = addFuzzy(this.sourceInPosition, sampleDuration);
    }

    this.sourceOutPosition = sampleBoundary;
  }

  private setInMarkAtSampleBoundary(sampleBoundary: FuzzyValue) {
    if (this.sourceOutPosition != null && !lessThanFuzzy(sampleBoundary, this.sourceOutPosition)) {
      this.sourceOutPosition = null;
    }
    this.sourceInPosition = sampleBoundary;
  }

  private setOutMarkAtSampleBoundary(sampleBoundary: FuzzyValue) {
    if (this.sourceInPosition != null && !lessThanFuzzy(this.sourceInPosition, sampleBoundary)) {
      this.sourceInPosition = null;
    }
    this.sourceOutPosition = sampleBoundary;
  }

  setMarkOutFromTime(
    durationInMs: number,
    virtualClipStart: FuzzyValue | null,
    ms: number,
  ) {
    this.setMarkOutFromTimeInternal(durationInMs, virtualClipStart, ms);
    return ms;
  }

  private tryGetSampleBoundaryNearVirtualClipPosition(
    virtualClipStart: FuzzyValue | null,
    sampleDuration: FuzzyValue | null,
    virtualClipRange: MediaSelection,
    virtualClipPosition: FuzzyValue
  ): FuzzyValue | null {
    const clipStart = virtualClipStart;
    if (clipStart == null || sampleDuration == null) {
      return null;
    }
    const mediaPos = addFuzzy(clipStart, virtualClipPosition);
    const sampleIndex = Math.round(divideFuzzy(mediaPos, sampleDuration).approximate);
    let sampleBoundary = multiplyFuzzy(sampleDuration, fractionToFuzzy(frac(sampleIndex, 1)));
    const allowedRange = virtualClipRange;

    if (lessThanFuzzy(sampleBoundary, allowedRange.bounds.start)) {
      sampleBoundary = allowedRange.bounds.start;
    } else if (lessThanFuzzy(allowedRange.bounds.end, sampleBoundary)) {
      sampleBoundary = allowedRange.bounds.end;
    }

    return sampleBoundary;
  }

  private setMarkInFromTimeInternal(
    virtualClipDurationInMs: number,
    virtualClipStart: FuzzyValue | null,
    milliseconds: number
  ) {
    const ms = Math.min(Math.max(milliseconds, 0), virtualClipDurationInMs);
    // If no mark out present, set it explicitly
    if (!this.hasMarkOut()) {
      this.setRoughVirtualClipOutMarkInternal(virtualClipStart, virtualClipDurationInMs);
    }

    if (ms < this.getMarkOut(virtualClipDurationInMs, virtualClipStart)) {
      this.setRoughVirtualClipInMarkInternal(virtualClipStart, ms);
    } else if (!this.isDragging) {
      this.setRoughVirtualClipOutMarkInternal(virtualClipStart, virtualClipDurationInMs);
      this.setRoughVirtualClipInMarkInternal(virtualClipStart, ms);
    } else {
      this.setRoughVirtualClipInMarkInternal(
        virtualClipStart,
        this.getMarkOut(virtualClipDurationInMs, virtualClipStart) - MINIMUM_PARTIAL_DURATION
      );
    }
  }

  private setRoughVirtualClipInMarkInternal(virtualClipStart: FuzzyValue | null, ms: number) {
    const clipStart = virtualClipStart;
    invariant(clipStart != null, 'Need clip start');
    this.sourceInPosition = addFuzzy(clipStart, { approximate: ms / 1000 });
  }

  private setRoughVirtualClipOutMarkInternal(virtualClipStart: FuzzyValue | null, ms: number) {
    const clipStart = virtualClipStart;
    invariant(clipStart != null, 'Need clip start');
    this.sourceOutPosition = addFuzzy(clipStart, { approximate: ms / 1000 });
  }

  private setMarkOutFromTimeInternal(
    durationInMs: number,
    virtualClipStart: FuzzyValue | null,
    milliseconds: number,
  ) {
    const ms = Math.max(Math.min(milliseconds, durationInMs), MINIMUM_PARTIAL_DURATION);
    // if no mark in present, set it explicitly
    if (!this.hasMarkIn()) {
      this.setRoughVirtualClipInMarkInternal(virtualClipStart, 0);
    }

    if (ms >= this.getMarkIn(virtualClipStart)) {
      this.setRoughVirtualClipOutMarkInternal(virtualClipStart, ms);
    } else if (!this.isDragging) {
      this.setRoughVirtualClipInMarkInternal(virtualClipStart, 0);
      this.setRoughVirtualClipOutMarkInternal(virtualClipStart, ms);
    } else {
      this.setRoughVirtualClipOutMarkInternal(virtualClipStart, this.getMarkIn(virtualClipStart) + MINIMUM_PARTIAL_DURATION);
    }
  }

  setStartPoint(start: StartPoint) {
    this.startPointInternal = start;
  }

  get startPoint() {
    return this.startPointInternal;
  }

}
